import { useEffect, useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import StatCard from '@/components/base/StatCard';
import { useRole } from '@/context/RoleContext';
import { auditCategories } from '@/mocks/audit';
import { api } from '@/utils/api';
import {
  auditActorOptions,
  auditCategoryBreakdown,
  auditTotals,
  auditTopActors,
  filterAudit,
  groupByDay,
} from '@/utils/audit';
import type { AuditEvent } from '@/types/portal';
import AuditToolbar from './components/AuditToolbar';
import AuditTimeline from './components/AuditTimeline';
import AuditSummaryPanels from './components/AuditSummaryPanels';
import AuditDetailModal from './components/AuditDetailModal';

export default function AuditLog() {
  const { can, role } = useRole();
  const canView = can('audit.view');

  // The local calendar date, so "Today" and "recorded today" mean today.
  const todayKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!canView) return;
    api<AuditEvent[]>('/settings/audit')
      .then((data) => {
        setEvents(data);
        setLoadError('');
      })
      .catch((error: Error) => setLoadError(error.message))
      .finally(() => setLoading(false));
  }, [canView]);

  const [category, setCategory] = useState('All');
  const [actorId, setActorId] = useState('All actors');
  const [impact, setImpact] = useState('All impacts');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [notice, setNotice] = useState('');

  const baseFiltered = useMemo(
    () => filterAudit(events, { category: 'All', actorId, impact, query }),
    [events, actorId, impact, query],
  );

  const tabs = useMemo(
    () =>
      auditCategories.map((item) => ({
        id: item,
        label: item === 'All' ? 'All activity' : item,
        count: item === 'All' ? baseFiltered.length : baseFiltered.filter((e) => e.category === item).length,
      })),
    [baseFiltered],
  );

  const filtered = useMemo(
    () => (category === 'All' ? baseFiltered : baseFiltered.filter((e) => e.category === category)),
    [baseFiltered, category],
  );

  const groups = useMemo(() => groupByDay(filtered, todayKey), [filtered, todayKey]);

  const totals = auditTotals(events, todayKey);
  const actors = useMemo(() => auditTopActors(filtered), [filtered]);
  const actorOptions = useMemo(() => auditActorOptions(events), [events]);
  const breakdown = useMemo(() => auditCategoryBreakdown(filtered), [filtered]);
  const highImpact = useMemo(
    () => filtered.filter((e) => e.impact !== 'Standard'),
    [filtered],
  );

  const exportCsv = () => {
    const header = [
      'Timestamp',
      'Actor',
      'Role',
      'Action',
      'Category',
      'Resource',
      'Outcome',
      'Impact',
      'Market',
      'IP',
      'Reference',
    ];
    const rows = filtered.map((event) => [
      event.timestamp,
      event.actorName,
      event.actorRole,
      event.summary,
      event.category,
      `${event.resource} ${event.resourceId}`,
      event.outcome,
      event.impact,
      event.market,
      event.ip,
      event.reference,
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'audit-log.csv';
    link.click();
    URL.revokeObjectURL(url);
    setNotice(`Exported ${filtered.length} audit events to CSV.`);
  };

  if (!canView) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
            <i className="ri-lock-line" />
          </span>
          <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">
            Audit Log is restricted
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            Your current role ({role.name}) cannot read the audit trail. Switch to an Agency
            Administrator or Finance user to review every consequential action.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <p className="text-xs font-label font-medium uppercase tracking-wider text-foreground-400">
            Settings
          </p>
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Audit Log
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Every consequential action — who did it, when and what changed.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" icon="ri-download-2-line" onClick={exportCsv}>
            Export CSV
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total events"
          value={String(totals.total)}
          icon="ri-history-line"
          tone="primary"
          hint={`${totals.today} recorded today`}
        />
        <StatCard
          label="High-impact events"
          value={String(totals.highImpact)}
          icon="ri-alert-line"
          tone="accent"
          hint="Sensitive or critical grants"
        />
        <StatCard
          label="Actors involved"
          value={String(totals.actors)}
          icon="ri-user-voice-line"
          tone="secondary"
          hint="Distinct people acting"
        />
        <StatCard
          label="Needs review"
          value={String(totals.attention)}
          icon="ri-shield-cross-line"
          tone="neutral"
          hint="Failed or denied attempts"
        />
      </div>

      {notice ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
        <i className="ri-information-line" />
        The audit trail is append-only and read-only. Every entry is retained for compliance and cannot
        be edited or deleted.
      </div>

      <div className="mt-6">
        <AuditToolbar
          tabs={tabs}
          category={category}
          onCategory={setCategory}
          actors={actorOptions}
          actorId={actorId}
          onActor={setActorId}
          impact={impact}
          onImpact={setImpact}
          query={query}
          onQuery={setQuery}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <h3 className="font-heading text-sm font-semibold text-foreground-950">Timeline</h3>
            <span className="text-xs text-foreground-500">
              Showing {filtered.length} of {totals.total}
            </span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 py-16 text-sm text-foreground-500">
              <i className="ri-loader-4-line mr-2 animate-spin" />
              Loading the audit trail…
            </div>
          ) : loadError ? (
            <div className="rounded-lg border border-primary-200 bg-primary-50 py-12 text-center text-sm text-primary-800">
              <i className="ri-error-warning-line mr-1" />
              {loadError}
            </div>
          ) : (
            <AuditTimeline groups={groups} onSelect={setSelected} />
          )}
        </div>

        <div className="sticky top-6">
          <AuditSummaryPanels
            categories={breakdown}
            total={filtered.length}
            actors={actors}
            highImpact={highImpact}
            onSelect={setSelected}
          />
        </div>
      </div>

      <AuditDetailModal event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}